import { cn } from 'components/UI/cn'
import React, { ButtonHTMLAttributes, DetailedHTMLProps } from 'react'
import { Spinner } from 'react-bootstrap'
import { Tooltip } from 'components/Tooltip/tooltip'
import { useDs2026Buttons } from 'components/FbUI/Ds2026ButtonContext'

export type FbButtonVariants =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'danger'
  | 'danger-outline'
  | 'transparent'
  | 'outline'
  | 'primary-outline'

export type FbButtonSizes = 'sm' | 'md' | 'lg' | 'xs' | 'mdlg' | 'icon'

export interface IFbButtonProps extends Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  'ref'
> {
  loading?: boolean
  variant?: FbButtonVariants
  size?: FbButtonSizes
  tooltip?: string
}

const FbButton = React.forwardRef((props: IFbButtonProps, ref: any) => {
  const { className, loading, variant, disabled, tooltip, ...rest } = props
  const size = props.size ?? 'md'
  const ds2026 = useDs2026Buttons()

  return (
    <Tooltip delay={[0, 0]} content={tooltip}>
      <button
        ref={ref}
        className={cn(
          'flex-shrink-0 min-w-0 text-base rounded-md font-medium min-h-10 px-3 flex items-center text-center gap-2 justify-center disabled:border-transparent disabled:pointer-events-none',
          // Primary variant (default)
          ds2026
            ? 'bg-ds2026-btn-primary-bg text-ds2026-btn-primary-text hover:bg-ds2026-btn-primary-hover-bg'
            : 'bg-brand text-white hover:bg-brand-hover hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)] active:bg-brand-pressed active:shadow-[inset_0px_4px_4px_-1px_rgba(12,12,13,0.05)]',
          {
            // Secondary variant
            '!bg-white text-fg-secondary !border !border-line hover:!bg-white hover:text-fg-secondary hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)] active:!bg-surface-raised active:!border-fg-secondary active:text-fg active:shadow-none':
              variant === 'secondary' && !ds2026,
            'bg-[#FDB81E] text-black hover:bg-[#FDB81E]': variant === 'warning',
            'bg-red-600 text-white hover:bg-red-700': variant === 'danger',
            'bg-transparent border-solid border-1 border-red-400 text-red-400 hover:text-red-400':
              variant === 'danger-outline',
            'bg-transparent border-solid border-0 hover:shadow-none text-black':
              variant === 'transparent',
            'bg-transparent border-solid text-black border-line border-[1px] hover:bg-surface-hover hover:shadow-none':
              variant === 'outline' && !ds2026,
            // DS2026 neutral button (outline + secondary share the look).
            // `!border` is required: an unlayered global bootstrap rule forces
            // border-color #dee2e6 and beats Tailwind's layered border-color
            // utilities, so without !important the Gray-300 stroke token
            // silently renders as bootstrap gray (matches the secondary/disabled
            // variants above, which already use !border for the same reason).
            '!border !border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg hover:text-ds2026-btn-outline-hover-text':
              ds2026 && (variant === 'outline' || variant === 'secondary'),
            'bg-surface-hover border-solid border-brand text-brand border-[1px] hover:bg-surface-active':
              variant === 'primary-outline',
            'bg-surface-disabled text-fg-secondary hover:text-fg-secondary hover:bg-surface-disabled hover:shadow-none':
              disabled && !ds2026,
            // DS2026 disabled — Gray 100 fill + border, Gray 400 text
            '!bg-ds2026-btn-disabled-bg !text-ds2026-btn-disabled-text !border !border-ds2026-btn-disabled-border hover:!bg-ds2026-btn-disabled-bg hover:shadow-none':
              disabled && ds2026,
            'min-h-14': size === 'lg',
            'min-h-12': size === 'mdlg',
            'min-h-10': size === 'md',
            'min-h-8 text-sm': size === 'sm',
            'min-h-6 text-xs px-2': size === 'xs',
            'min-h-0 p-0': size === 'icon',
          },
          className
        )}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <div className="flex items-center w-full justify-center">
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          </div>
        ) : (
          <>{props.children}</>
        )}
      </button>
    </Tooltip>
  )
})
FbButton.displayName = 'FbButton'

export default FbButton

export const IconMiniButton = React.forwardRef(
  (props: IFbButtonProps, ref: React.Ref<HTMLButtonElement>) => (
    <FbButton
      ref={ref}
      {...props}
      className={cn(
        'relative h-11 !min-h-0 rounded-lg px-3.5 py-2 text-sm font-semibold leading-5',
        props.className
      )}
    />
  )
)
IconMiniButton.displayName = 'IconMiniButton'
